import { useState, useEffect } from "react";
import ActionPanel from "./ActionPanel";
import ActionModal from "./ActionModal";
import MoveActionForm from "./MoveActionForm";
import { authenticatedPlayerFetch } from "../utils/api";
import { useTimer } from "../contexts/TimerContext";
import { 
  PlayerRole, 
  type Role, 
  SpaceshipAction, 
  AgencyAction, 
  SPACESHIP_ACTIONS, 
  AGENCY_ACTIONS,
  getSpaceshipActionCost,
  getAgencyActionCost,
  MAX_ACTIONS_PER_TURN,
  MAX_ALLOWED_SPACESHIP_DAMAGE,
  ActionStatus,
  COMM_LEVEL_MAX,
  SCAN_LEVEL_MAX,
  MAX_LANDING_SITE_POINTS,
  allowsSpaceshipActionCollaboration,
  allowsAgencyActionCollaboration,
  getActionEmoji,
} from "../shared/constants";
import { buildPlayerName } from "../shared/helper";

interface ActionButton {
  label: string;
  onClick: () => void;
  backgroundColor?: string;
  disabled?: boolean;
  showNotification?: boolean;
}

interface PlayerActionsProps {
  playerId: string;
  playerRole: Role;
  gameStatus: string | null;
  actionCount: number | null;
  spaceshipDamage?: number | null;
  spaceshipShieldsOn?: boolean | null;
  teamName?: string;
  gameName?: string;
  landingGear?: string | null;
  commLevel?: number | null;
  scanLevel?: number | null;
  totalLandingSitePoints?: number;
  currentTurn?: number | null;
  timerEnabled?: boolean; // When false, timer-based limitations are disabled
  limitActions?: boolean; // When false, action limit-based limitations are disabled
  currentLocation?: string | null;
  rightButtons?: ActionButton[];
}

export default function PlayerActions({ playerId, playerRole, gameStatus, actionCount, spaceshipDamage, spaceshipShieldsOn, teamName, gameName, landingGear, commLevel, scanLevel, totalLandingSitePoints, currentTurn, timerEnabled = true, limitActions = true, currentLocation, rightButtons = [] }: PlayerActionsProps) {
  const { isPaused, timerSeconds } = useTimer();
  const [selectedAction, setSelectedAction] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [landingGearOptions, setLandingGearOptions] = useState<string[]>([]);
  const [landingGearValue, setLandingGearValue] = useState<string>(landingGear || "");
  const [isLoadingLandingGear, setIsLoadingLandingGear] = useState<boolean>(false);
  const [landingGearError, setLandingGearError] = useState<string>("");
  const [collaborate, setCollaborate] = useState<boolean>(false);
  const [collaboratorOptions, setCollaboratorOptions] = useState<{ id: string; name: string }[]>([]);
  const [selectedCollaboratorIds, setSelectedCollaboratorIds] = useState<string[]>([]);
  const [agencyPlayers, setAgencyPlayers] = useState<{ id: string; teamName: string; landingSitePoints: number }[]>([]);
  const [isLoadingAgencies, setIsLoadingAgencies] = useState<boolean>(false);
  const [agencyError, setAgencyError] = useState<string>("");

  // Get actions based on role (exclude Adapt Landing Gear)
  // For spaceship, reorder: Scan first, Move last
  const actions = playerRole === PlayerRole.SPACESHIP 
    ? (() => {
        const filtered = SPACESHIP_ACTIONS.filter(action => action !== SpaceshipAction.ADAPT_LANDING_GEAR);
        // Reorder: Scan first, Move last, others in between
        const scan = filtered.find(a => a === SpaceshipAction.SCAN);
        const move = filtered.find(a => a === SpaceshipAction.MOVE);
        const others = filtered.filter(a => a !== SpaceshipAction.SCAN && a !== SpaceshipAction.MOVE);
        return [
          ...(scan ? [scan] : []),
          ...others,
          ...(move ? [move] : [])
        ];
      })()
    : AGENCY_ACTIONS;

  const isGameActive = (gameStatus ?? "").toUpperCase() === "ACTIVE";
  const isTurnZero = (currentTurn ?? 0) === 0;
  const isTimerZero = (timerSeconds ?? 0) === 0;
  const actionsMade = actionCount ?? 0;
  const remainingActions = MAX_ACTIONS_PER_TURN - actionsMade;
  const isEliminated = playerRole === PlayerRole.SPACESHIP && (spaceshipDamage ?? 0) >= MAX_ALLOWED_SPACESHIP_DAMAGE;

  useEffect(() => {
    setLandingGearValue(landingGear || "");
  }, [landingGear]);

  useEffect(() => {
    if (selectedAction === SpaceshipAction.ADAPT_LANDING_GEAR) {
      setLandingGearValue(landingGear || "");
      setLandingGearError("");
    }
  }, [selectedAction, landingGear]);

  useEffect(() => {
    setCollaborate(false);
    setSelectedCollaboratorIds([]);
    setAgencyError("");
  }, [selectedAction]);

  // Fetch agency players when Build Landing Site is selected
  useEffect(() => {
    if (selectedAction !== AgencyAction.BUILD_LANDING_SITE || !gameName) {
      setAgencyPlayers([]);
      return;
    }
    let cancelled = false;
    const fetchAgencies = async () => {
      setIsLoadingAgencies(true);
      setAgencyError("");
      try {
        const res = await authenticatedPlayerFetch(`/players/by-game-name?game_name=${encodeURIComponent(gameName)}`);
        if (!res.ok) {
          const errData = await res.json().catch(() => ({}));
          throw new Error(errData.detail || "Failed to fetch agency players");
        }
        const data = await res.json();
        if (cancelled) return;
        const agencies = Array.isArray(data)
          ? data.filter((p: any) => p.role === PlayerRole.AGENCY)
          : [];
        
        // Fetch landing site points for each agency
        const agenciesWithPoints = await Promise.all(
          agencies.map(async (p: any) => {
            try {
              const pointsRes = await authenticatedPlayerFetch(
                `/players/landing-site-investments?player_id=${encodeURIComponent(p.player_id)}`
              );
              if (pointsRes.ok) {
                const pointsData = await pointsRes.json();
                const totalPoints = Array.isArray(pointsData)
                  ? pointsData.reduce((sum: number, inv: any) => sum + (inv.count || 0), 0)
                  : 0;
                return {
                  id: p.player_id,
                  teamName: p.team_name || "",
                  landingSitePoints: totalPoints,
                };
              }
            } catch (err) {
              console.error(`Failed to fetch landing site points for ${p.player_id}:`, err);
            }
            return {
              id: p.player_id,
              teamName: p.team_name || "",
              landingSitePoints: 0,
            };
          })
        );
        
        if (cancelled) return;
        // Sort agencies alphabetically by team name
        const sortedAgencies = agenciesWithPoints.sort((a, b) => {
          const nameA = a.teamName || "";
          const nameB = b.teamName || "";
          return nameA.localeCompare(nameB);
        });
        setAgencyPlayers(sortedAgencies);
      } catch (error) {
        if (cancelled) return;
        console.error("Failed to fetch agency players:", error);
        setAgencyError(error instanceof Error ? error.message : "Failed to load agency players");
        setAgencyPlayers([]);
      } finally {
        if (!cancelled) {
          setIsLoadingAgencies(false);
        }
      }
    };
    fetchAgencies();
    return () => {
      cancelled = true;
    };
  }, [selectedAction, gameName]);

  useEffect(() => {
    if (!collaborate) {
      setSelectedCollaboratorIds([]);
    }
  }, [collaborate]);


  useEffect(() => {
    if (playerRole !== PlayerRole.SPACESHIP || !gameName) {
      setLandingGearOptions([]);
      return;
    }
    let cancelled = false;
    const fetchTeams = async () => {
      setIsLoadingLandingGear(true);
      setLandingGearError("");
      try {
        const res = await authenticatedPlayerFetch(`/teams/by-game-name?game_name=${encodeURIComponent(gameName)}`);
        if (!res.ok) {
          const errData = await res.json().catch(() => ({}));
          throw new Error(errData.detail || "Failed to fetch teams");
        }
        const data = await res.json();
        if (cancelled) return;
        const codes = Array.isArray(data)
          ? data
              .map((team: { team_code_name?: string }) => team.team_code_name)
              .filter((code: string | undefined): code is string => Boolean(code))
          : [];
        setLandingGearOptions(codes);
      } catch (error) {
        if (cancelled) return;
        console.error("Failed to fetch team code names:", error);
        setLandingGearError(error instanceof Error ? error.message : "Failed to load team code names");
        setLandingGearOptions([]);
      } finally {
        if (!cancelled) {
          setIsLoadingLandingGear(false);
        }
      }
    };
    fetchTeams();
    return () => {
      cancelled = true;
    };
  }, [playerRole, gameName]);

  useEffect(() => {
    if (!gameName || !playerId) {
      setCollaboratorOptions([]);
      return;
    }
    let cancelled = false;
    const fetchPlayers = async () => {
      try {
        const res = await authenticatedPlayerFetch(`/players/by-game-name?game_name=${encodeURIComponent(gameName)}`);
        if (!res.ok) {
          const errData = await res.json().catch(() => ({}));
          throw new Error(errData.detail || "Failed to fetch players");
        }
        const data = await res.json();
        if (cancelled) return;
        const options = Array.isArray(data)
          ? data
              .filter((p: any) => p.role === playerRole && p.player_id !== playerId)
              .map((p: any) => ({
                id: p.player_id,
                name: buildPlayerName(p.team_name || "", p.role),
              }))
          : [];
      setCollaboratorOptions(options);
      setSelectedCollaboratorIds((prev) => prev.filter(id => options.some(option => option.id === id)));
      } catch (error) {
        if (cancelled) return;
        console.error("Failed to fetch collaborators:", error);
        setCollaboratorOptions([]);
      setSelectedCollaboratorIds([]);
      }
    };
    fetchPlayers();
    return () => {
      cancelled = true;
    };
  }, [gameName, playerId, playerRole]);

  const submitActionOnly = async (value: string, actionType: string, collaboratorNames?: string[]) => {
    const res = await authenticatedPlayerFetch("/players/action", {
      method: "POST",
      body: JSON.stringify({
        player_id: playerId,
        action: actionType,
        details: value,
        status: ActionStatus.PENDING,
        collaborators: collaboratorNames && collaboratorNames.length > 0 ? collaboratorNames.join(", ") : null,
      })
    });

    if (!res.ok) {
      const errorData = await res.json().catch(() => ({}));
      throw new Error(errorData.detail || "Failed to submit action");
    }
  };

  // Create buttons for each action with cost displayed
  const leftButtons = actions.map((action) => {
    // Get the cost for this action
    const cost = playerRole === PlayerRole.SPACESHIP
      ? getSpaceshipActionCost(action as SpaceshipAction)
      : getAgencyActionCost(action as AgencyAction);
    
    // Declare disabled variable
    let disabled: boolean;
    
    // Disable Adapt Landing Gear entirely, regardless of timer or limit actions logic
    if (action === SpaceshipAction.ADAPT_LANDING_GEAR) {
      disabled = true;
    } else {
      // Disable if game is not active, if turn is 0, if timer is paused/zero (only if timer enabled), if cost exceeds remaining actions (only if limit actions enabled), or if player is eliminated
      const timerLimitation = timerEnabled && (isPaused || isTimerZero);
      const actionLimitLimitation = limitActions && (cost > remainingActions);
      disabled = !isGameActive || isTurnZero || timerLimitation || actionLimitLimitation || isEliminated;
      
      // For Fix Damage action, also disable if damage is 0
      if (action === SpaceshipAction.FIX_DAMAGE && (spaceshipDamage ?? 0) === 0) {
        disabled = true;
      }
      if (action === SpaceshipAction.RAISE_SHIELDS && !!spaceshipShieldsOn) {
        disabled = true;
      }
    }
    if (playerRole === PlayerRole.AGENCY) {
      if (action === AgencyAction.IMPROVE_COMMUNICATION && (commLevel ?? 0) >= COMM_LEVEL_MAX) {
        disabled = true;
      }
      if (action === AgencyAction.IMPROVE_SCAN && (scanLevel ?? 0) >= SCAN_LEVEL_MAX) {
        disabled = true;
      }
    }
    
    // Customize label based on action type
    const emoji = getActionEmoji(action as SpaceshipAction | AgencyAction);
    let label: string;
    if (playerRole === PlayerRole.SPACESHIP) {
      // Format spaceship actions as: "<emoji> <Action>\nCost: <cost>"
      if (action === SpaceshipAction.FIX_DAMAGE) {
        label = `${emoji} ${action}\nCurrent damage: ${spaceshipDamage ?? 0}/${MAX_ALLOWED_SPACESHIP_DAMAGE}\nCost: ${cost}`;
      } else if (action === SpaceshipAction.MOVE) {
        const locationText = currentLocation || "No location set";
        label = `${emoji} ${action}\nCurrent location: ${locationText}\nCost: ${cost}`;
      } else {
        label = `${emoji} ${action}\nCost: ${cost}`;
      }
    } else if (action === AgencyAction.IMPROVE_SCAN) {
      label = `${emoji} Improve Scan\nCurrent Level: ${scanLevel ?? 0}\nCost: ${cost}`;
    } else if (action === AgencyAction.IMPROVE_COMMUNICATION) {
      label = `${emoji} Improve Communication\nCurrent Level: ${commLevel ?? 0}\nCost: ${cost}`;
    } else if (action === AgencyAction.SCAN) {
      label = `${emoji} Scan\nCurrent level: ${scanLevel ?? 0}\nCost: ${cost}`;
    } else if (action === AgencyAction.BUILD_LANDING_SITE) {
      label = `${emoji} Build Landing Site\nCost: ${cost}`;
    } else {
      label = `${emoji} ${action} [${cost}]`;
    }
    
    return {
      label: label,
      onClick: () => {
        if (disabled) return;
        if (action === SpaceshipAction.RAISE_SHIELDS) {
          handleRaiseShields();
          return;
        }
        if (action === SpaceshipAction.FIX_DAMAGE) {
          handleFixDamage();
          return;
        }
        if (playerRole === PlayerRole.AGENCY) {
          if (action === AgencyAction.IMPROVE_COMMUNICATION) {
            handleImproveCommunication();
            return;
          }
          if (action === AgencyAction.IMPROVE_SCAN) {
            handleImproveScan();
            return;
          }
        }
        setSelectedAction(action);
      },
      backgroundColor: "#6f42c1",
      disabled
    };
  });

  const handleActionSubmit = async (value: string, actionType: string) => {
    setIsSubmitting(true);
    try {
      const collaboratorNames = collaborate
        ? selectedCollaboratorIds
            .map(id => collaboratorOptions.find(option => option.id === id)?.name)
            .filter((name): name is string => Boolean(name))
        : undefined;
      const collaboratorsPayload = collaboratorNames && collaboratorNames.length > 0 ? collaboratorNames : undefined;
      await submitActionOnly(value, actionType, collaboratorsPayload);
      // Action created successfully, WebSocket will update the action count
      // Also dispatch event to refresh ViewActions component
      window.dispatchEvent(new CustomEvent('actionCreated'));
      setSelectedAction(null);
      setSelectedCollaboratorIds([]);
    } catch (error) {
      console.error("Error submitting action:", error);
      alert(error instanceof Error ? error.message : "Failed to submit action");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleRaiseShields = async () => {
    setIsSubmitting(true);
    try {
      const res = await authenticatedPlayerFetch("/players/shields/on", {
        method: "POST",
        body: JSON.stringify({
          player_id: playerId
        })
      });
      if (!res.ok) {
        const errorData = await res.json().catch(() => ({}));
        throw new Error(errorData.detail || "Failed to raise shields");
      }

      await submitActionOnly("", SpaceshipAction.RAISE_SHIELDS);
      window.dispatchEvent(new CustomEvent('actionCreated'));
      setSelectedAction(null);
    } catch (error) {
      console.error("Error raising shields:", error);
      alert(error instanceof Error ? error.message : "Failed to raise shields");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleFixDamage = async () => {
    if (!teamName) {
      alert("Team information missing. Please refresh and try again.");
      return;
    }
    setIsSubmitting(true);
    try {
      const res = await authenticatedPlayerFetch("/players/damage/dec", {
        method: "POST",
        body: JSON.stringify({
          team_name: teamName,
          player_role: PlayerRole.SPACESHIP
        })
      });
      if (!res.ok) {
        const errorData = await res.json().catch(() => ({}));
        throw new Error(errorData.detail || "Failed to fix damage");
      }

      await submitActionOnly("", SpaceshipAction.FIX_DAMAGE);
      window.dispatchEvent(new CustomEvent('actionCreated'));
      setSelectedAction(null);
    } catch (error) {
      console.error("Error fixing damage:", error);
      alert(error instanceof Error ? error.message : "Failed to fix damage");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleImproveCommunication = async () => {
    if (!teamName) {
      alert("Team information missing. Please refresh and try again.");
      return;
    }
    setIsSubmitting(true);
    try {
      const res = await authenticatedPlayerFetch("/players/comm/inc", {
        method: "POST",
        body: JSON.stringify({
          team_name: teamName,
          player_role: PlayerRole.AGENCY
        })
      });
      if (!res.ok) {
        const errorData = await res.json().catch(() => ({}));
        throw new Error(errorData.detail || "Failed to improve communication");
      }

      await submitActionOnly("", AgencyAction.IMPROVE_COMMUNICATION);
      window.dispatchEvent(new CustomEvent('actionCreated'));
      setSelectedAction(null);
    } catch (error) {
      console.error("Error improving communication:", error);
      alert(error instanceof Error ? error.message : "Failed to improve communication");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleImproveScan = async () => {
    if (!teamName) {
      alert("Team information missing. Please refresh and try again.");
      return;
    }
    setIsSubmitting(true);
    try {
      const res = await authenticatedPlayerFetch("/players/scan/inc", {
        method: "POST",
        body: JSON.stringify({
          team_name: teamName,
          player_role: PlayerRole.AGENCY
        })
      });
      if (!res.ok) {
        const errorData = await res.json().catch(() => ({}));
        throw new Error(errorData.detail || "Failed to improve scan");
      }

      await submitActionOnly("", AgencyAction.IMPROVE_SCAN);
      window.dispatchEvent(new CustomEvent('actionCreated'));
      setSelectedAction(null);
    } catch (error) {
      console.error("Error improving scan:", error);
      alert(error instanceof Error ? error.message : "Failed to improve scan");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleMoveSubmit = (route: string) => {
    handleActionSubmit(route, SpaceshipAction.MOVE);
  };

  const handleScanSubmit = (cellsToScan: string) => {
    const actionType = playerRole === PlayerRole.SPACESHIP 
      ? SpaceshipAction.SCAN 
      : AgencyAction.SCAN;
    handleActionSubmit(cellsToScan, actionType);
  };

  const handleCloseModal = () => {
    setSelectedAction(null);
  };

  // Determine which form to show based on selected action
  const actionAllowsCollaboration = (action: string | null) => {
    if (!action) return false;
    if (playerRole === PlayerRole.SPACESHIP && Object.values(SpaceshipAction).includes(action as SpaceshipAction)) {
      return allowsSpaceshipActionCollaboration(action as SpaceshipAction);
    }
    if (playerRole === PlayerRole.AGENCY && Object.values(AgencyAction).includes(action as AgencyAction)) {
      return allowsAgencyActionCollaboration(action as AgencyAction);
    }
    return false;
  };

  const renderActionForm = () => {
    const showCollaborateToggle = selectedAction ? actionAllowsCollaboration(selectedAction) : false;
    const showCollaboratorSelect = showCollaborateToggle && collaborate && collaboratorOptions.length > 0;

    if (selectedAction === SpaceshipAction.MOVE) {
      return (
        <MoveActionForm
          onSubmit={handleMoveSubmit}
          onCancel={handleCloseModal}
          isSubmitting={isSubmitting}
          label="Specify route:"
          placeholder="Enter route"
          showCollaborateToggle={showCollaborateToggle}
          isCollaborating={collaborate}
          onToggleCollaborate={setCollaborate}
          showCollaboratorSelect={showCollaboratorSelect}
          collaboratorOptions={collaboratorOptions}
          selectedCollaborators={selectedCollaboratorIds}
          showMovementRules={true}
          onCollaboratorsChange={setSelectedCollaboratorIds}
          currentLocation={currentLocation}
          isSpaceshipMove={true}
        />
      );
    }
    
    // Handle SCAN action for both spaceship and agency
    if (selectedAction === SpaceshipAction.SCAN || selectedAction === AgencyAction.SCAN) {
      return (
        <MoveActionForm
          onSubmit={handleScanSubmit}
          onCancel={handleCloseModal}
          isSubmitting={isSubmitting}
          label={playerRole === PlayerRole.SPACESHIP ? "Square to scan" : "Squares to scan"}
          placeholder={playerRole === PlayerRole.SPACESHIP ? "Square" : "Enter Squares to scan"}
          showCollaborateToggle={playerRole === PlayerRole.SPACESHIP ? false : showCollaborateToggle}
          isCollaborating={playerRole === PlayerRole.SPACESHIP ? false : collaborate}
          onToggleCollaborate={setCollaborate}
          showCollaboratorSelect={playerRole === PlayerRole.SPACESHIP ? false : showCollaboratorSelect}
          collaboratorOptions={collaboratorOptions}
          selectedCollaborators={selectedCollaboratorIds}
          onCollaboratorsChange={setSelectedCollaboratorIds}
          showScanRules={false}
          showAgencyScanRules={playerRole === PlayerRole.AGENCY}
          currentLocation={currentLocation}
          isSpaceshipScan={playerRole === PlayerRole.SPACESHIP}
          isAgencyScan={playerRole === PlayerRole.AGENCY}
          scanLevel={playerRole === PlayerRole.AGENCY ? scanLevel : null}
        />
      );
    }

    if (selectedAction === SpaceshipAction.ADAPT_LANDING_GEAR) {
      return (
        <form onSubmit={handleLandingGearSubmit} style={{ display: "flex", flexDirection: "column", gap: "0.75rem" }}>
          <label style={{ fontWeight: 600, color: "#212529" }}>
            Target Landing Gear
            <select
              value={landingGearValue}
              onChange={(e) => setLandingGearValue(e.target.value)}
              disabled={isSubmitting || isLoadingLandingGear}
              style={{
                width: "100%",
                padding: "0.5rem",
                marginTop: "0.25rem",
                borderRadius: "4px",
                border: "1px solid #ced4da",
                fontSize: "1rem"
              }}
            >
              <option value="">{isLoadingLandingGear ? "Loading options..." : "Select target"}</option>
              {landingGearOptions.map((code) => (
                <option key={code} value={code}>
                  {code}
                </option>
              ))}
            </select>
          </label>
          {landingGearError && (
            <div style={{ color: "#dc3545", fontSize: "0.85rem" }}>{landingGearError}</div>
          )}
          <div style={{ display: "flex", gap: "0.75rem", justifyContent: "flex-end" }}>
            <button
              type="button"
              onClick={handleCloseModal}
              disabled={isSubmitting}
              style={{
                padding: "0.5rem 1rem",
                borderRadius: "4px",
                border: "1px solid #ced4da",
                backgroundColor: "white",
                cursor: isSubmitting ? "not-allowed" : "pointer"
              }}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting || isLoadingLandingGear}
              style={{
                padding: "0.5rem 1rem",
                borderRadius: "4px",
                border: "none",
                backgroundColor: "#6f42c1",
                color: "white",
                cursor: isSubmitting || isLoadingLandingGear ? "not-allowed" : "pointer"
              }}
            >
              {isSubmitting ? "Updating..." : "Update Landing Gear"}
            </button>
          </div>
        </form>
      );
    }

    if (selectedAction === AgencyAction.BUILD_LANDING_SITE) {
      return (
        <div style={{ display: "flex", flexDirection: "column", gap: "0.75rem" }}>
          <label style={{ fontWeight: 600, color: "#212529", marginBottom: "0.5rem" }}>
            Choose Target Agency
          </label>
          {isLoadingAgencies ? (
            <div style={{ padding: "1rem", textAlign: "center", color: "#6c757d" }}>
              Loading agencies...
            </div>
          ) : agencyError ? (
            <div style={{ color: "#dc3545", fontSize: "0.85rem", padding: "0.5rem" }}>
              {agencyError}
            </div>
          ) : agencyPlayers.length === 0 ? (
            <div style={{ padding: "1rem", textAlign: "center", color: "#6c757d" }}>
              No agencies found
            </div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}>
              {agencyPlayers.map((agency) => {
                const isAtMax = agency.landingSitePoints >= MAX_LANDING_SITE_POINTS;
                const isDisabled = isSubmitting || isAtMax;
                return (
                  <button
                    key={agency.id}
                    type="button"
                    onClick={() => handleBuildLandingSiteSubmit(agency.id, agency.teamName)}
                    disabled={isDisabled}
                    style={{
                      padding: "0.75rem 1rem",
                      borderRadius: "4px",
                      border: "1px solid #ced4da",
                      backgroundColor: isDisabled ? "#e9ecef" : "white",
                      color: isDisabled ? "#6c757d" : "#212529",
                      cursor: isDisabled ? "not-allowed" : "pointer",
                      textAlign: "left",
                      fontSize: "1rem",
                      transition: "background-color 0.2s",
                      opacity: isDisabled ? 0.6 : 1,
                    }}
                    onMouseEnter={(e) => {
                      if (!isDisabled) {
                        e.currentTarget.style.backgroundColor = "#f8f9fa";
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (!isDisabled) {
                        e.currentTarget.style.backgroundColor = "white";
                      }
                    }}
                    title={isAtMax ? `This team has reached the maximum landing site points (${MAX_LANDING_SITE_POINTS})` : undefined}
                  >
                    {agency.teamName} {isAtMax && `(${agency.landingSitePoints}/${MAX_LANDING_SITE_POINTS} - MAX)`}
                  </button>
                );
              })}
            </div>
          )}
          <div style={{ display: "flex", gap: "0.75rem", justifyContent: "flex-end", marginTop: "0.5rem" }}>
            <button
              type="button"
              onClick={handleCloseModal}
              disabled={isSubmitting}
              style={{
                padding: "0.5rem 1rem",
                borderRadius: "4px",
                border: "1px solid #ced4da",
                backgroundColor: "white",
                cursor: isSubmitting ? "not-allowed" : "pointer"
              }}
            >
              Cancel
            </button>
          </div>
        </div>
      );
    }

    
    return null;
  };

  const handleBuildLandingSiteSubmit = async (targetPlayerId: string, teamName: string) => {
    setIsSubmitting(true);
    try {
      // Submit action with target agency's team name in details
      await submitActionOnly(teamName, AgencyAction.BUILD_LANDING_SITE);
      window.dispatchEvent(new CustomEvent('actionCreated'));
      setSelectedAction(null);
    } catch (error) {
      console.error("Error building landing site:", error);
      alert(error instanceof Error ? error.message : "Failed to build landing site");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleLandingGearSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!landingGearValue) {
      setLandingGearError("Please select a landing gear target.");
      return;
    }
    setLandingGearError("");
    setIsSubmitting(true);
    try {
      const res = await authenticatedPlayerFetch("/players/landing-gear/update", {
        method: "POST",
        body: JSON.stringify({
          player_id: playerId,
          landing_gear: landingGearValue
        })
      });
      if (!res.ok) {
        const errorData = await res.json().catch(() => ({}));
        throw new Error(errorData.detail || "Failed to update landing gear");
      }
      await submitActionOnly(landingGearValue, SpaceshipAction.ADAPT_LANDING_GEAR);
      window.dispatchEvent(new CustomEvent('actionCreated'));
      setSelectedAction(null);
    } catch (error) {
      console.error("Error adapting landing gear:", error);
      alert(error instanceof Error ? error.message : "Failed to adapt landing gear");
    } finally {
      setIsSubmitting(false);
    }
  };

  const actionCounter = actionCount !== null ? (
    <>
      <span style={{ fontSize: "1.2rem", fontWeight: "600", color: "#007bff" }}>Actions Made This Turn</span>
      <hr style={{
        width: "100%",
        margin: 0,
        border: "none",
        borderTop: "1px solid rgba(0, 123, 255, 0.3)",
        opacity: 0.7
      }} />
      <span style={{ fontSize: "0.85rem", fontWeight: "400", color: "#007bff" }}>
        <strong>{actionCount} / {MAX_ACTIONS_PER_TURN}</strong>
      </span>
    </>
  ) : null;

  return (
    <>
      <ActionPanel leftButtons={leftButtons} rightButtons={rightButtons} actionCounter={actionCounter} />
      <ActionModal
        isOpen={selectedAction !== null}
        onClose={handleCloseModal}
        title={selectedAction || "Action"}
      >
        {renderActionForm()}
      </ActionModal>
    </>
  );
}

